import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import useUserContext from 'Context/UserContext';
import useAffordance from 'Hooks/useAffordance';
import fr from 'facet-rest';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';

const RECENT_KEY_PRESS_TIMEOUT_MS = 200;

const SearchBarStateContext = React.createContext(undefined);
const SearchBarDispatchContext = React.createContext(undefined);

function getSearchResultDOMId(searchResultResource) {
  return `search-result-id-${searchResultResource.id()}`;
}

function getSuggestedFilterOptionDOMId(suggestedFilterOptionId) {
  return `search-filter-option-${suggestedFilterOptionId}`;
}

function checkSearchTermLength(searchTerm) {
  return searchTerm.length > 0;
}

function shouldShowResults(state, searchTerm) {
  return checkSearchTermLength(searchTerm);
}

const getSelectionId = (state, isNext) => {
  const {
    suggestedFilterOptions,
    searchResponse,
    selectionDOMId,
  } = state;

  const searchResponseResources = searchResponse?.resources().list() || [];
  const showSearchResults = shouldShowResults(state, state.searchTerm);

  const options = [
    ...suggestedFilterOptions.map((v) => {
      return getSuggestedFilterOptionDOMId(v.id());
    }),
    ...searchResponseResources.filter(() => {
      return showSearchResults;
    }).map((v) => {
      return getSearchResultDOMId(v);
    }),
  ];

  if (options.length === 0) {
    return null;
  }

  if (options.length === 1) {
    return options[0];
  }

  // If nothing is selected then it will appear like the first item is selected, so choose the *next* item
  if (selectionDOMId === null && isNext) {
    return options[1];
  } if (selectionDOMId === null) {
    return options[options.length - 1];
  }

  const selectedIndex = options.findIndex((v) => {
    return v === selectionDOMId;
  });

  if (isNext) {
    if (selectedIndex === -1) {
      return options[0];
    }

    if (selectedIndex === options.length - 1) {
      return options[0];
    }

    return options[selectedIndex + 1];
  }

  if (selectedIndex === -1) {
    return options[options.length - 1];
  }

  if (selectedIndex === 0) {
    return options[options.length - 1];
  }

  return options[selectedIndex - 1];
};

const getFirstSelectableId = (state) => {
  const {
    suggestedFilterOptions,
    searchResponse,
  } = state;

  const searchResponseResources = searchResponse?.resources().list() || [];
  const showSearchResults = shouldShowResults(state, state.searchTerm);
  let options = [];
  if (suggestedFilterOptions) {
    options = [
      ...suggestedFilterOptions?.map((v) => {
        return getSuggestedFilterOptionDOMId(v.id());
      }),
      ...searchResponseResources.filter(() => {
        return showSearchResults;
      }).map((v) => {
        return getSearchResultDOMId(v);
      }),
    ];
  }


  if (options.length === 0) {
    return null;
  }

  return options[0];
};

function getSuggestedFilterOptions(state, searchTerm) {
  const {
    emptySearchResponse,
  } = state;

  const searchTermLength = searchTerm.length;

  return emptySearchResponse?.meta()
    .query(fr.queries.getSearchResults.name)
    .field('searchType')
    .options()
    .list()
    .filter((option) => {
      if (searchTermLength === 0) {
        return true;
      }

      return (option.attr('id').lastIndexOf(searchTerm, 0) === 0);
    });
}

function init() {
  return {
    searchesMade: 0,
    initialized: false,
    emptySearchResponse: null,
    suggestedFilterOptions: [],
    searchTerm: '',
    searchType: null,
    searchResultsOpen: false,
    isSearching: false,
    searchResponse: null,
    responseError: null,
    selectionDOMId: null,
    showSearchResults: false,
  };
}

function reducer(state, action) {
  switch (action.type) {
    case 'setEmptySearchResponse': {
      return {
        ...state,
        emptySearchResponse: action.emptySearchResponse,
        initialized: true,
      };
    }
    case 'setSearchTerm': {
      const optional = {};

      if (action.searchType) {
        optional.searchType = action.searchType;
      }

      return {
        ...state,
        ...optional,
        searchTerm: action.searchTerm,
        selectionDOMId: null,
        suggestedFilterOptions: getSuggestedFilterOptions(state, action.searchTerm),
      };
    }
    case 'clearSearchType': {
      return {
        ...state,
        searchType: null,
      };
    }
    case 'setSearchType': {
      return {
        ...state,
        searchType: action.searchType,
        searchTerm: '',
      };
    }
    case 'openSearchResults': {
      document.getElementById('global-search-input').focus();

      return {
        ...state,
        searchResultsOpen: true,
        suggestedFilterOptions: getSuggestedFilterOptions(state, state.searchTerm),
        selectionDOMId: null,
      };
    }
    case 'closeSearchResults': {
      document.getElementById('global-search-input').blur();

      return {
        ...state,
        searchesMade: 0, // Reset searches made for next time
        searchType: null,
        searchTerm: '',
        searchResultsOpen: false,
      };
    }
    case 'setSearchResponse': {
      return {
        ...state,
        searchesMade: state.searchesMade + 1,
        searchResponse: action.searchResponse,
        isSearching: false,
      };
    }
    case 'setResponseError': {
      return {
        ...state,
        responseError: action.responseError,
        isSearching: false,
      };
    }
    case 'setSearchStarted': {
      return {
        ...state,
        isSearching: true,
      };
    }
    case 'setSelectionDOMId': {
      return {
        ...state,
        selectionDOMId: action.selectionDOMId,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

const globalKeyPressMap = {};

function checkHasPressedKeyRecently(keyCode) {
  const now = new Date().valueOf();
  const existingPressTime = globalKeyPressMap[keyCode];

  return existingPressTime && (now - existingPressTime < RECENT_KEY_PRESS_TIMEOUT_MS);
}

function SearchBarProvider({ children }) {
  const [state, dispatch] = React.useReducer(reducer, null, init);
  const eventTracking = useEventTrackingLogEvent();
  const { userResource } = useUserContext();
  const emptySearchLink = userResource?.relationship(fr.relationships.searchResults.name).link('related');

  function onGlobalKeyUp(evt) {
    const eventKeyCode = evt.code;

    let openSearchMethod = null;

    switch (eventKeyCode) {
      case 'KeyK':
        if (evt.getModifierState('Alt')) {
          openSearchMethod = 'AltPlusK';
        }
        break;
      // eslint-disable-next-line no-fallthrough
      case 'ShiftLeft':
      case 'ShiftRight':
        if (checkHasPressedKeyRecently(eventKeyCode)) {
          openSearchMethod = 'ShiftShift';
        }
        globalKeyPressMap[eventKeyCode] = new Date().valueOf();
        break;
      /* no default */
    }

    if (openSearchMethod) {
      setTimeout(() => {
        eventTracking.logEvent('search > opened', { entryMethod: openSearchMethod });
        dispatch({ type: 'openSearchResults' });
      }, 50);
    }
  }

  useAffordance(emptySearchLink, {
    onFetch: (resp) => {
      dispatch({ type: 'setEmptySearchResponse', emptySearchResponse: resp });
    },
  });

  useComponentDidMount(() => {
    document.addEventListener('keyup', onGlobalKeyUp, { capture: true });
  }, () => {
    document.removeEventListener('keyup', onGlobalKeyUp, { capture: true });
  });

  return (
    <SearchBarStateContext.Provider value={state}>
      <SearchBarDispatchContext.Provider value={dispatch}>
        {children}
      </SearchBarDispatchContext.Provider>
    </SearchBarStateContext.Provider>
  );
}

function useSearchBarState() {
  const context = React.useContext(SearchBarStateContext);

  if (context === undefined) {
    throw new Error('useSearchBarState must be used within a SearchBarProvider');
  }

  return context;
}

function useSearchBarDispatch() {
  const context = React.useContext(SearchBarDispatchContext);

  if (context === undefined) {
    throw new Error('useSearchBarDispatch must be used within a SearchBarProvider');
  }

  return context;
}

const fetchResults = async (dispatch, state, searchTerm, searchType, eventTracking) => {
  const { emptySearchResponse } = state;

  try {
    dispatch({ type: 'setSearchStarted' });
    eventTracking.logEvent('search > search started', {
      searchType,
      searchTerm,
    });
    const searchResponse = await emptySearchResponse.query(fr.queries.getSearchResults.name, (link) => {
      link.withQueryParams()
        .limit(5)
        .equal('searchTerm', searchTerm);

      if (searchType) {
        link.equal('searchType', searchType);
      }

      return link.get();
    });
    dispatch({ type: 'setSearchResponse', searchResponse });
  } catch (err) {
    dispatch({ type: 'setResponseError', responseError: err });
  }
};

const debouncedFetchResults = _.debounce(fetchResults, 250);

const inputKeyPressMap = {};
const onInputKeyDown = (dispatch, state, { logEvent }) => (evt) => {
  const eventKeyCode = evt.nativeEvent.code;
  inputKeyPressMap[eventKeyCode] = true;
  const numKeysPressed = Object.keys(inputKeyPressMap).length;

  switch (eventKeyCode) {
    case 'Backspace':
      if (state.searchTerm.length === 0) {
        logEvent('search > clear search type', {
          keyboard: true,
        });
        dispatch({ type: 'setSearchType', searchType: null });
      }
      break;
    case 'ArrowUp':
      if (numKeysPressed === 1) {
        evt.preventDefault();
        evt.stopPropagation();
        logEvent('search > select previous option');
        dispatch({ type: 'setSelectionDOMId', selectionDOMId: getSelectionId(state) });
      }
      break;
    case 'ArrowDown':
      if (numKeysPressed === 1) {
        evt.preventDefault();
        evt.stopPropagation();
        logEvent('search > select next option');
        dispatch({ type: 'setSelectionDOMId', selectionDOMId: getSelectionId(state, true) });
      }
      break;
    case 'Escape':
      logEvent('search > closed', {
        searchesMade: state.searchesMade,
      });
      dispatch({ type: 'closeSearchResults' });
      break;
    case 'Enter':
      logEvent('search > use enter to make search selection');
      if (state.selectionDOMId === null) {
        const firstSelectableId = getFirstSelectableId(state);
        if (firstSelectableId === null) {
          return;
        }

        document.getElementById(firstSelectableId).click();
      } else {
        document.getElementById(state.selectionDOMId).click();
      }
      break;
    /* no default */
  }
};

const onInputKeyUp = () => (evt) => {
  delete inputKeyPressMap[evt.nativeEvent.code];
};

const onSelectSearchType = (dispatch, state, searchType, eventTracking) => () => {
  const { searchTerm } = state;
  if (searchType === null) {
    dispatch({ type: 'clearSearchType', searchType });
  } else {
    dispatch({ type: 'setSearchType', searchType });
  }

  if (checkSearchTermLength(searchTerm)) {
    debouncedFetchResults(dispatch, state, searchTerm, searchType, eventTracking);
  }
};

const onChangeInput = (dispatch, state, eventTracking) => (evt) => {
  const searchTerm = evt.target.value;
  const { searchType } = state;

  if (searchType === null && searchTerm.slice(-1) === ':') {
    const searchTermWithoutColon = searchTerm.slice(0, -1);
    const filterOptions = getSuggestedFilterOptions(state, searchTermWithoutColon);
    if (filterOptions.length === 1) {
      eventTracking.logEvent('search > search type set by typing colon', {
        searchTypeMatchedWith: searchTermWithoutColon,
        searchType: filterOptions[0].id(),
      });
      dispatch({
        type: 'setSearchTerm',
        searchTerm: '',
        searchType: filterOptions[0].id(),
      });

      return;
    }
  }

  dispatch({ type: 'setSearchTerm', searchTerm });

  if (checkSearchTermLength(searchTerm)) {
    debouncedFetchResults(dispatch, state, searchTerm, searchType, eventTracking);
  }
};

const onCloseResults = (dispatch, state, eventTracking, searchInputRef) => (evt) => {
  if (searchInputRef.current && searchInputRef.current.contains(evt.target)) {
    return;
  }

  eventTracking.logEvent('search > closed', {
    searchesMade: state.searchesMade,
  });

  dispatch({ type: 'closeSearchResults' });
};

const onOpenResults = (dispatch, state, eventTracking) => () => {
  if (!state.initialized) {
    return;
  }

  eventTracking.logEvent('search > opened', { entryMethod: 'click' });
  dispatch({ type: 'openSearchResults' });
};

SearchBarProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  getSearchResultDOMId,
  getFirstSelectableId,
  getSuggestedFilterOptionDOMId,
  shouldShowResults,
  onSelectSearchType,
  onInputKeyDown,
  onInputKeyUp,
  onChangeInput,
  onOpenResults,
  onCloseResults,
  SearchBarProvider,
  useSearchBarState,
  useSearchBarDispatch,
};
